import { z } from "zod";
import { Id } from "./ids.js";

/**
 * Hub ↔ server sync contracts (architecture §5.4, ADR-04): commands go up, a change feed comes down.
 * No CRDT, no generic replication. Ordering comes from per-device sequence numbers and entity versions;
 * the device clock is informational only.
 */

/** `entity.verb`, e.g. `order.create`, `order.set-line`. */
export const CommandType = z.string().regex(/^[a-z][a-z-]*\.[a-z][a-z-]*$/);

export const SyncCommand = z.object({
  /** Client-generated UUIDv7; the server applies a command id at most once. */
  id: Id,
  deviceId: Id,
  /** Strictly increasing per device; the server processes a device's commands in this order. */
  seq: z.int().positive(),
  type: CommandType,
  entityId: Id,
  /** Entity version the hub saw when it created the command (absent for creates). */
  baseVersion: z.int().nonnegative().nullable(),
  /** Fields this command changes; decides between automatic merge and a conflict. */
  fields: z.array(z.string().min(1)).min(1),
  /**
   * The values of `fields` as the hub saw them. When the server moved on since `baseVersion`, the command still
   * applies if those fields are unchanged (someone edited other fields); otherwise it is a conflict (F4-T11).
   */
  baseValues: z.record(z.string(), z.json()).optional(),
  payload: z.json(),
  /** Device wall clock, recorded for the audit trail only. */
  issuedAt: z.iso.datetime({ offset: true }),
});
export type SyncCommand = z.infer<typeof SyncCommand>;

export const SyncResult = z.discriminatedUnion("status", [
  z.object({ commandId: Id, status: z.literal("applied"), entityVersion: z.int().nonnegative() }),
  /** Valid command that the business rules refuse (e.g. archived dish); shown in the review queue with the reason. */
  z.object({ commandId: Id, status: z.literal("rejected"), reason: z.string().min(1) }),
  /** Same field changed on the server since baseVersion; the server value stays until the manager decides. */
  z.object({
    commandId: Id,
    status: z.literal("conflict"),
    fields: z.array(z.string()),
    serverVersion: z.int().nonnegative(),
  }),
]);
export type SyncResult = z.infer<typeof SyncResult>;

/** One entry of the downward change feed. */
export const ChangeEntry = z.object({
  seq: z.int().positive(),
  entityType: z.string().min(1),
  entityId: Id,
  version: z.int().nonnegative(),
  data: z.json(),
  /** The entity was archived or left the hub's window: the hub drops its copy. */
  removed: z.boolean().default(false),
});
export type ChangeEntry = z.infer<typeof ChangeEntry>;

/**
 * One page of the feed. The hub stores `next` as its position even when every entry of the page was filtered out
 * for it (special portions need permission, architecture §5.2), so it never asks for the same rows again.
 */
export const FeedPage = z.object({
  entries: z.array(ChangeEntry),
  next: z.int().nonnegative(),
  more: z.boolean(),
});
export type FeedPage = z.infer<typeof FeedPage>;

/** What a hub may do offline (architecture §5.3), each through the normal services and rules. */
export const HUB_COMMANDS = [
  "order.submit",
  "order.approve",
  "order.return",
  "order.adjust",
  "order.cancel",
  "task.start",
  "task.ready",
  "transfer.send",
  "transfer.receive",
] as const;
export type HubCommand = (typeof HUB_COMMANDS)[number];

/** Manager decision on a command in the review queue: keep the server value, or apply the hub's after all. */
export const SyncResolve = z.object({
  decision: z.enum(["keep-server", "use-hub"]),
  note: z.string().max(500),
});
export type SyncResolve = z.infer<typeof SyncResolve>;

/**
 * Version of this protocol. Hubs send it as `x-hub-protocol`; the server refuses anything older than it
 * accepts with 426, and the hub then installs its update before syncing again (architecture §5.7, F4-T13).
 */
export const SYNC_PROTOCOL = 1;
export const MIN_HUB_PROTOCOL = 1;
